/**
 * HelpAlert — primary instructional component for iTow.
 *
 * Use this for top-of-page or section-level guidance copy. When `persistKey`
 * is set the Alert is dismissible and the user's choice persists in
 * localStorage; the close X is suppressed when `persistKey` is omitted so
 * warning/required guidance keeps nagging.
 *
 * Do not use this for transient action feedback — that's <Snackbar>'s job.
 */
import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import { useDismissedHelp } from './useDismissedHelp';

export interface HelpAlertProps {
  /** Severity. Defaults to 'info'. */
  severity?: 'info' | 'success' | 'warning' | 'error';
  /** MUI Alert variant. Defaults to 'outlined'. */
  variant?: 'standard' | 'outlined' | 'filled';
  /** Optional title rendered via <AlertTitle>. */
  title?: string;
  /** Body content. ReactNode so the caller can pass JSX (links, lists). */
  children: React.ReactNode;
  /**
   * When set, the Alert is dismissible and the dismissal persists in
   * localStorage under `itow.help.{persistKey}.dismissed`. Once
   * dismissed, the component renders nothing (returns null) until the
   * user clears their localStorage.
   *
   * When omitted, the Alert is permanent — there is no close button.
   * Use this for warning-severity alerts that should keep nagging.
   */
  persistKey?: string;
  /** Outer Box sx override — defaults to bottom margin so the layer below the Alert flows naturally. */
  sx?: object;
}

export default function HelpAlert({
  severity = 'info',
  variant = 'outlined',
  title,
  children,
  persistKey,
  sx,
}: HelpAlertProps): React.ReactElement | null {
  const [dismissed, setDismissed] = useDismissedHelp(persistKey);

  if (persistKey && dismissed) {
    return null;
  }

  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Alert
        severity={severity}
        variant={variant}
        onClose={persistKey ? setDismissed : undefined}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </Alert>
    </Box>
  );
}
